/**
 * NeedsAttention — HITL gates waiting for the founder.
 *
 * One row per pending item. Severity colour survives on the leading
 * dot, otherwise monochrome. Click → onOpenCard.
 */
import { cn } from '../../lib/utils';
import type { NeedsAttentionItem, NeedsAttentionResponse } from './dashboard-types';

const SEVERITY_DOT: Record<NeedsAttentionItem['severity'], string> = {
  low: 'bg-foreground/40',
  medium: 'bg-amber-500',
  high: 'bg-rose-500',
};

export interface NeedsAttentionProps {
  data: NeedsAttentionResponse | undefined;
  isLoading?: boolean | undefined;
  onOpenCard?: ((cardId: string, boardId?: string) => void) | undefined;
  className?: string | undefined;
}

export function NeedsAttention({ data, isLoading, onOpenCard, className }: NeedsAttentionProps) {
  const items = data?.items ?? [];
  return (
    <section
      aria-labelledby="attention-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="attention-heading" className="text-sm font-semibold text-foreground">
          Needs attention
        </h2>
        <span className="text-2xs text-muted-foreground">{items.length} waiting</span>
      </header>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          Nothing's waiting on you.
        </p>
      ) : (
        <ul className="divide-y divide-border text-xs">
          {items.map((i) => (
            <li key={i.id} className="flex items-center gap-2 py-2">
              <span
                aria-hidden
                className={cn('h-2 w-2 shrink-0 rounded-full', SEVERITY_DOT[i.severity])}
              />
              <button
                type="button"
                onClick={() => onOpenCard?.(i.card_id)}
                className="flex-1 truncate text-left text-xs text-foreground transition-colors hover:underline"
              >
                {i.card_title}
              </button>
              <span className="truncate text-2xs text-muted-foreground" title={i.reason}>
                {i.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
